import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Divider,
  IconButton,
  Link,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { blue, amber } from "@mui/material/colors";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";

export default function Footer() {
  const year = new Date().getFullYear();
  const AMBER_ACCENT = (amber && (amber.A400 || amber[600])) || "#ffb300";

  const navLinks = [
    { label: "Home", to: "/" },
    { label: "About Us", to: "/about" },
    { label: "Admission", to: "/admission" },
    { label: "Fee Structure", to: "/feestructure" },
    { label: "Circular", to: "/circular" },
    { label: "Contact Us", to: "/contact" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        overflow: "hidden",
        bgcolor: "background.paper",
        // Calm, subtle gradient background
        backgroundImage: `linear-gradient(180deg, ${alpha(blue[700], 0.06)} 0%, ${alpha(
          AMBER_ACCENT,
          0.04
        )} 100%)`,
        // Soft corner glow
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `radial-gradient(420px 260px at 0% 0%, ${alpha("#fff", .18)} 0%, transparent 60%),
                       radial-gradient(460px 280px at 100% 100%, ${alpha("#fff", .10)} 0%, transparent 65%)`,
          pointerEvents: "none",
        },
        // Elegant top accent bar
        "&:after": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          height: 3,
          background: `linear-gradient(90deg, ${alpha(blue[700], 0.9)}, ${alpha(AMBER_ACCENT, 0.9)})`,
        },
        borderTop: `1px solid ${alpha(blue[700], 0.15)}`,
      }}
    >
      {/* Top */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
        <Grid container spacing={{ xs: 2, sm: 3, md: 6 }}>
          {/* Brand + About */}
          <Grid item xs={12} md={4}>
            <Stack spacing={1.25}>
              <Box
                component={RouterLink}
                to="/"
                aria-label="AISECT Inter College — Home"
                sx={{ display: "inline-flex", alignItems: "center", gap: 1, textDecoration: "none" }}
              >
                <Box
                  component="img"
                  src="/assets/images/logo/aisect_inter_college_logo.png"
                  alt="AISECT Inter College Logo"
                  sx={{ height: 42, width: "auto", display: "block" }}
                  loading="lazy"
                />
              </Box>

              <Typography
                sx={{ color: "text.secondary", fontSize: { xs: 13, sm: 13.5 }, lineHeight: 1.65 }}
              >
                We’re always looking for motivated learners and mentors.
                Don’t be shy—introduce yourself!
              </Typography>

              <Stack direction="row" spacing={0.75}>
                {[
                  { href: "https://www.facebook.com/", label: "Facebook", icon: <FacebookIcon fontSize="small" /> },
                  { href: "https://www.instagram.com/", label: "Instagram", icon: <InstagramIcon fontSize="small" /> },
                  { href: "https://www.linkedin.com/", label: "LinkedIn", icon: <LinkedInIcon fontSize="small" /> },
                ].map((s) => (
                  <IconButton
                    key={s.label}
                    component="a"
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      color: blue[800],
                      bgcolor: alpha("#fff", 0.7),
                      border: `1px solid ${alpha(blue[700], 0.25)}`,
                      backdropFilter: "saturate(140%) blur(4px)",
                      transition: "all .2s ease",
                      "&:hover": {
                        bgcolor: alpha("#fff", 0.9),
                        borderColor: blue[700],
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    {s.icon}
                  </IconButton>
                ))}
              </Stack>
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 900,
                mb: 1,
                fontSize: { xs: 13.5, sm: 14 },
                background: `linear-gradient(90deg, ${blue[800]} 0%, ${AMBER_ACCENT} 100%)`,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Our College
            </Typography>
            <nav aria-label="Footer navigation">
              <Grid container spacing={0.5}>
                {navLinks.map((l) => (
                  <Grid key={l.to} item xs={6}>
                    <Link
                      component={RouterLink}
                      to={l.to}
                      underline="none"
                      sx={{
                        display: "inline-block",
                        color: "text.secondary",
                        fontSize: { xs: 13, sm: 13.5 },
                        py: 0.25,
                        transition: "color .2s ease, transform .2s ease",
                        "&:hover": {
                          color: blue[800],
                          transform: "translateX(2px)",
                        },
                      }}
                    >
                      {l.label}
                    </Link>
                  </Grid>
                ))}
              </Grid>
            </nav>
          </Grid>

          {/* Contact */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 900,
                mb: 1,
                fontSize: { xs: 13.5, sm: 14 },
                background: `linear-gradient(90deg, ${blue[800]} 0%, ${AMBER_ACCENT} 100%)`,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Get Contact
            </Typography>

            <Stack spacing={0.6} sx={{ color: "text.secondary", fontSize: { xs: 13, sm: 13.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon sx={{ fontSize: 18, color: blue[700] }} />
                <Link
                  href="tel:+917479708899"
                  underline="none"
                  sx={{ color: "text.secondary", "&:hover": { color: blue[800] } }}
                >
                  (+91) 7479708899
                </Link>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon sx={{ fontSize: 18, color: blue[700] }} />
                <Link
                  href="tel:+918002748899"
                  underline="none"
                  sx={{ color: "text.secondary", "&:hover": { color: blue[800] } }}
                >
                  (+91) 8002748899
                </Link>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <EmailIcon sx={{ fontSize: 18, color: blue[700] }} />
                <Link
                  href="mailto:aisectintercollege2023@gmail.com"
                  underline="none"
                  sx={{ color: "text.secondary", "&:hover": { color: blue[800] } }}
                >
                  aisectintercollege2023@gmail.com
                </Link>
              </Stack>
            </Stack>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 900,
                mt: 1.5,
                mb: 0.5,
                fontSize: { xs: 13, sm: 13.5 },
                color: "text.primary",
              }}
            >
              Full Address
            </Typography>

            <address style={{ margin: 0 }}>
              <Stack spacing={0.75} sx={{ color: "text.secondary", fontSize: { xs: 13, sm: 13.5 } }}>
                <Stack direction="row" spacing={1.2} alignItems="flex-start">
                  <LocationOnIcon sx={{ fontSize: 18, color: blue[700] }} />
                  <span>
                    <strong>Main Campus:</strong> Vill: Jonhiya (Tarba–Kharba), P.O.: Maheshra,
                    P.S.: Daru, Dist.: Hazaribagh, Jharkhand – 825313
                  </span>
                </Stack>
                <Stack direction="row" spacing={1.2} alignItems="flex-start">
                  <LocationOnIcon sx={{ fontSize: 18, color: blue[700] }} />
                  <span>
                    <strong>City Campus:</strong> AISECT Inter College, Matwari Chowk,
                    Hazaribagh, Jharkhand – 825301
                  </span>
                </Stack>
              </Stack>
            </address>
          </Grid>
        </Grid>
      </Container>

      {/* Gradient divider */}
      <Divider
        sx={{
          opacity: 0.9,
          borderColor: alpha(blue[700], 0.15),
          "&::before": {
            content: '""',
            display: "block",
            height: 3,
            background: `linear-gradient(90deg, ${alpha(blue[700], 0.5)}, ${alpha(AMBER_ACCENT, 0.5)})`,
            borderRadius: 2,
            transform: "translateY(-1px)",
          },
        }}
      />

      {/* Bottom */}
      <Container maxWidth="lg" sx={{ py: 1.5 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={0.75}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            sx={{
              fontSize: { xs: 12.5, sm: 13 },
              textAlign: { xs: "center", sm: "left" },
              color: "text.secondary",
            }}
          >
            ©2023-{year}{" "}
            <Link
              component={RouterLink}
              to="/"
              underline="hover"
              sx={{ color: "text.primary", "&:hover": { color: blue[800] } }}
            >
              AISECT Inter College
            </Link>{" "}
            — All rights reserved.
          </Typography>

          {/* <Stack direction="row" spacing={1}>
            <Link
              href="#!"
              underline="hover"
              sx={{ fontSize: { xs: 12.5, sm: 13 }, color: "text.secondary", "&:hover": { color: blue[800] } }}
            >
              Terms of Service
            </Link>
            <Typography sx={{ color: "text.disabled" }}>•</Typography>
            <Link
              href="#!"
              underline="hover"
              sx={{ fontSize: { xs: 12.5, sm: 13 }, color: "text.secondary", "&:hover": { color: blue[800] } }}
            >
              Privacy Policy
            </Link>
          </Stack> */}
        </Stack>
      </Container>
    </Box>
  );
}



// import React from "react";
// import { Link as RouterLink } from "react-router-dom";
// import {
//   Box,
//   Container,
//   Grid,
//   Stack,
//   Typography,
//   Divider,
//   IconButton,
//   Link,
// } from "@mui/material";
// import { alpha } from "@mui/material/styles";
// import { red, orange } from "@mui/material/colors";
// import FacebookIcon from "@mui/icons-material/Facebook";
// import InstagramIcon from "@mui/icons-material/Instagram";
// import LinkedInIcon from "@mui/icons-material/LinkedIn";
// import PhoneIcon from "@mui/icons-material/Phone";
// import EmailIcon from "@mui/icons-material/Email";
// import LocationOnIcon from "@mui/icons-material/LocationOn";

// export default function Footer() {
//   const year = new Date().getFullYear();
//   const ORANGE_ACCENT = (orange && (orange.A400 || orange[600])) || "#ff9100";

//   const navLinks = [
//     { label: "Home", to: "/" },
//     { label: "About Us", to: "/about" },
//     { label: "Admission", to: "/admission" },
//     { label: "Event", to: "/event" },
//     { label: "Circular", to: "/circular" },
//     { label: "Contact Us", to: "/contact" },
//   ];

//   return (
//     <Box
//       component="footer"
//       sx={{
//         position: "relative",
//         overflow: "hidden",
//         bgcolor: "background.paper",
//         // Warm, subtle gradient background
//         backgroundImage: `linear-gradient(180deg, ${alpha(red[600], 0.06)} 0%, ${alpha(
//           ORANGE_ACCENT,
//           0.04
//         )} 100%)`,
//         // Soft corner glow
//         "&:before": {
//           content: '""',
//           position: "absolute",
//           inset: 0,
//           background:
//             `radial-gradient(420px 260px at 0% 0%, ${alpha("#fff", .18)} 0%, transparent 60%),
//              radial-gradient(460px 280px at 100% 100%, ${alpha("#fff", .10)} 0%, transparent 65%)`,
//           pointerEvents: "none",
//         },
//         // Elegant top accent bar
//         "&:after": {
//           content: '""',
//           position: "absolute",
//           left: 0,
//           right: 0,
//           top: 0,
//           height: 3,
//           background: `linear-gradient(90deg, ${alpha(red[600], 0.9)}, ${alpha(ORANGE_ACCENT, 0.9)})`,
//         },
//         borderTop: `1px solid ${alpha(red[600], 0.15)}`,
//       }}
//     >
//       {/* Top */}
//       <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
//         <Grid container spacing={{ xs: 2, sm: 3, md: 6 }}>
//           {/* Brand + About */}
//           <Grid item xs={12} md={4}>
//             <Stack spacing={1.25}>
//               <Box
//                 component={RouterLink}
//                 to="/"
//                 aria-label="AISECT Inter College — Home"
//                 sx={{ display: "inline-flex", alignItems: "center", gap: 1, textDecoration: "none" }}
//               >
//                 <Box
//                   component="img"
//                   src="/assets/images/logo/aisect_inter_college_logo.png"
//                   alt="AISECT Inter College Logo"
//                   sx={{ height: 42, width: "auto", display: "block" }}
//                   loading="lazy"
//                 />
//               </Box>

//               <Typography
//                 sx={{ color: "text.secondary", fontSize: { xs: 13, sm: 13.5 }, lineHeight: 1.65 }}
//               >
//                 We’re always looking for motivated learners and mentors.
//                 Don’t be shy—introduce yourself!
//               </Typography>

//               <Stack direction="row" spacing={0.75}>
//                 {[{
//                   href: "https://www.facebook.com/",
//                   label: "Facebook",
//                   icon: <FacebookIcon fontSize="small" />
//                 }, {
//                   href: "https://www.instagram.com/",
//                   label: "Instagram",
//                   icon: <InstagramIcon fontSize="small" />
//                 }, {
//                   href: "https://www.linkedin.com/",
//                   label: "LinkedIn",
//                   icon: <LinkedInIcon fontSize="small" />
//                 }].map((s) => (
//                   <IconButton
//                     key={s.label}
//                     component="a"
//                     href={s.href}
//                     target="_blank"
//                     rel="noopener noreferrer"
//                     aria-label={s.label}
//                     size="small"
//                     sx={{
//                       borderRadius: 2,
//                       color: red[700],
//                       bgcolor: alpha("#fff", 0.7),
//                       border: `1px solid ${alpha(red[600], 0.25)}`,
//                       backdropFilter: "saturate(140%) blur(4px)",
//                       transition: "all .2s ease",
//                       "&:hover": {
//                         bgcolor: alpha("#fff", 0.9),
//                         borderColor: red[600],
//                         transform: "translateY(-1px)",
//                       },
//                     }}
//                   >
//                     {s.icon}
//                   </IconButton>
//                 ))}
//               </Stack>
//             </Stack>
//           </Grid>

//           {/* Quick Links */}
//           <Grid item xs={12} sm={6} md={4}>
//             <Typography
//               variant="subtitle2"
//               sx={{
//                 fontWeight: 900,
//                 mb: 1,
//                 fontSize: { xs: 13.5, sm: 14 },
//                 background: `linear-gradient(90deg, ${red[700]} 0%, ${ORANGE_ACCENT} 100%)`,
//                 WebkitBackgroundClip: "text",
//                 backgroundClip: "text",
//                 color: "transparent",
//               }}
//             >
//               Our College
//             </Typography>
//             <nav aria-label="Footer navigation">
//               <Grid container spacing={0.5}>
//                 {navLinks.map((l) => (
//                   <Grid key={l.to} item xs={6}>
//                     <Link
//                       component={RouterLink}
//                       to={l.to}
//                       underline="none"
//                       sx={{
//                         display: "inline-block",
//                         color: "text.secondary",
//                         fontSize: { xs: 13, sm: 13.5 },
//                         py: 0.25,
//                         transition: "color .2s ease, transform .2s ease",
//                         "&:hover": {
//                           color: red[700],
//                           transform: "translateX(2px)",
//                         },
//                       }}
//                     >
//                       {l.label}
//                     </Link>
//                   </Grid>
//                 ))}
//               </Grid>
//             </nav>
//           </Grid>

//           {/* Contact */}
//           <Grid item xs={12} sm={6} md={4}>
//             <Typography
//               variant="subtitle2"
//               sx={{
//                 fontWeight: 900,
//                 mb: 1,
//                 fontSize: { xs: 13.5, sm: 14 },
//                 background: `linear-gradient(90deg, ${red[700]} 0%, ${ORANGE_ACCENT} 100%)`,
//                 WebkitBackgroundClip: "text",
//                 backgroundClip: "text",
//                 color: "transparent",
//               }}
//             >
//               Get Contact
//             </Typography>

//             <Stack spacing={0.6} sx={{ color: "text.secondary", fontSize: { xs: 13, sm: 13.5 } }}>
//               <Stack direction="row" spacing={1} alignItems="center">
//                 <PhoneIcon sx={{ fontSize: 18, color: red[600] }} />
//                 <Link href="tel:+917479708899" underline="none" sx={{ color: "text.secondary", "&:hover": { color: red[700] } }}>
//                   (+91) 7479708899
//                 </Link>
//               </Stack>
//               <Stack direction="row" spacing={1} alignItems="center">
//                 <EmailIcon sx={{ fontSize: 18, color: red[600] }} />
//                 <Link href="mailto:aisectintercollege2023@gmail.com" underline="none" sx={{ color: "text.secondary", "&:hover": { color: red[700] } }}>
//                   aisectintercollege2023@gmail.com
//                 </Link>
//               </Stack>
//             </Stack>

//             <Typography
//               variant="subtitle2"
//               sx={{
//                 fontWeight: 900,
//                 mt: 1.5,
//                 mb: 0.5,
//                 fontSize: { xs: 13, sm: 13.5 },
//                 color: "text.primary",
//               }}
//             >
//               Full Address
//             </Typography>

//             <address style={{ margin: 0 }}>
//               <Stack spacing={0.75} sx={{ color: "text.secondary", fontSize: { xs: 13, sm: 13.5 } }}>
//                 <Stack direction="row" spacing={1.2} alignItems="flex-start">
//                   <LocationOnIcon sx={{ fontSize: 18, color: red[600] }} />
//                   <span>
//                     <strong>Main Campus:</strong> Vill: Jonhiya (Tarba–Kharba), P.O.: Maheshra,
//                     P.S.: Daru, Dist.: Hazaribagh, Jharkhand – 825313
//                   </span>
//                 </Stack>
//                 <Stack direction="row" spacing={1.2} alignItems="flex-start">
//                   <LocationOnIcon sx={{ fontSize: 18, color: red[600] }} />
//                   <span>
//                     <strong>City Campus:</strong> AISECT University, Matwari Chowk,
//                     Hazaribagh, Jharkhand – 825301
//                   </span>
//                 </Stack>
//               </Stack>
//             </address>
//           </Grid>
//         </Grid>
//       </Container>

//       {/* Gradient divider */}
//       <Divider
//         sx={{
//           opacity: 0.9,
//           borderColor: alpha(red[600], 0.15),
//           "&::before": {
//             content: '""',
//             display: "block",
//             height: 3,
//             background: `linear-gradient(90deg, ${alpha(red[600], 0.5)}, ${alpha(ORANGE_ACCENT, 0.5)})`,
//             borderRadius: 2,
//             transform: "translateY(-1px)",
//           },
//         }}
//       />

//       {/* Bottom */}
//       <Container maxWidth="lg" sx={{ py: 1.5 }}>
//         <Stack
//           direction={{ xs: "column", sm: "row" }}
//           spacing={0.75}
//           alignItems="center"
//           justifyContent="space-between"
//         >
//           <Typography
//             sx={{
//               fontSize: { xs: 12.5, sm: 13 },
//               textAlign: { xs: "center", sm: "left" },
//               color: "text.secondary",
//             }}
//           >
//             ©2023-{year}{" "}
//             <Link component={RouterLink} to="/" underline="hover" sx={{ color: "text.primary", "&:hover": { color: red[700] } }}>
//               AISECT Inter College
//             </Link>{" "}
//             — All rights reserved.
//           </Typography>

//           <Stack direction="row" spacing={1}>
//             <Link
//               href="#!"
//               underline="hover"
//               sx={{ fontSize: { xs: 12.5, sm: 13 }, color: "text.secondary", "&:hover": { color: red[700] } }}
//             >
//               Terms of Service
//             </Link>
//             <Typography sx={{ color: "text.disabled" }}>•</Typography>
//             <Link
//               href="#!"
//               underline="hover"
//               sx={{ fontSize: { xs: 12.5, sm: 13 }, color: "text.secondary", "&:hover": { color: red[700] } }}
//             >
//               Privacy Policy
//             </Link>
//           </Stack>
//         </Stack>
//       </Container>
//     </Box>
//   );
// }


// import React from "react";
// import { Link as RouterLink } from "react-router-dom";
// import {
//   Box,
//   Container,
//   Grid,
//   Stack,
//   Typography,
//   Divider,
//   IconButton,
//   Link,
// } from "@mui/material";
// import FacebookIcon from "@mui/icons-material/Facebook";
// import InstagramIcon from "@mui/icons-material/Instagram";
// import LinkedInIcon from "@mui/icons-material/LinkedIn";
// import PhoneIcon from "@mui/icons-material/Phone";
// import EmailIcon from "@mui/icons-material/Email";
// import LocationOnIcon from "@mui/icons-material/LocationOn";

// export default function Footer() {
//   const year = new Date().getFullYear();

//   const navLinks = [
//     { label: "Home", to: "/" },
//     { label: "About Us", to: "/about" },
//     { label: "Admission", to: "/admission" },
//     { label: "Event", to: "/event" },
//     { label: "Circular", to: "/circular" },
//     { label: "Contact Us", to: "/contact" },
//   ];

//   return (
//     <Box component="footer" sx={{ borderTop: 1, borderColor: "divider", bgcolor: "background.paper" }}>
//       {/* Top */}
//       <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
//         <Grid container spacing={{ xs: 2, sm: 3, md: 6 }}>
//           {/* Brand + About */}
//           <Grid item xs={12} md={4}>
//             <Stack spacing={1.25}>
//               <Box
//                 component={RouterLink}
//                 to="/"
//                 aria-label="AISECT Inter College — Home"
//                 sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
//               >
//                 <Box
//                   component="img"
//                   src="assets/images/logo/aisect_inter_college_logo.png"
//                   alt="AISECT Inter College Logo"
//                   sx={{ height: 42, width: "auto", display: "block" }}
//                   loading="lazy"
//                 />
//               </Box>

//               <Typography
//                 sx={{ color: "text.secondary", fontSize: { xs: 13, sm: 13.5 }, lineHeight: 1.6 }}
//               >
//                 We’re always looking for motivated learners and mentors.
//                 Don’t be shy—introduce yourself!
//               </Typography>

//               <Stack direction="row" spacing={0.5}>
//                 <IconButton
//                   component="a"
//                   href="https://www.facebook.com/"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   aria-label="Facebook"
//                   size="small"
//                 >
//                   <FacebookIcon fontSize="small" />
//                 </IconButton>
//                 <IconButton
//                   component="a"
//                   href="https://www.instagram.com/"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   aria-label="Instagram"
//                   size="small"
//                 >
//                   <InstagramIcon fontSize="small" />
//                 </IconButton>
//                 <IconButton
//                   component="a"
//                   href="https://www.linkedin.com/"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   aria-label="LinkedIn"
//                   size="small"
//                 >
//                   <LinkedInIcon fontSize="small" />
//                 </IconButton>
//               </Stack>
//             </Stack>
//           </Grid>

//           {/* Quick Links */}
//           <Grid item xs={12} sm={6} md={4}>
//             <Typography
//               variant="subtitle2"
//               sx={{ fontWeight: 800, mb: 1, fontSize: { xs: 13.5, sm: 14 } }}
//             >
//               Our College
//             </Typography>
//             <nav aria-label="Footer navigation">
//               <Grid container spacing={0.5}>
//                 {navLinks.map((l) => (
//                   <Grid key={l.to} item xs={6}>
//                     <Link
//                       component={RouterLink}
//                       to={l.to}
//                       underline="none"
//                       sx={{
//                         display: "inline-block",
//                         color: "text.secondary",
//                         fontSize: { xs: 13, sm: 13.5 },
//                         "&:hover": { color: "text.primary" },
//                         py: 0.25,
//                       }}
//                     >
//                       {l.label}
//                     </Link>
//                   </Grid>
//                 ))}
//               </Grid>
//             </nav>
//           </Grid>

//           {/* Contact */}
//           <Grid item xs={12} sm={6} md={4}>
//             <Typography
//               variant="subtitle2"
//               sx={{ fontWeight: 800, mb: 1, fontSize: { xs: 13.5, sm: 14 } }}
//             >
//               Get Contact
//             </Typography>

//             <Stack spacing={0.5} sx={{ color: "text.secondary", fontSize: { xs: 13, sm: 13.5 } }}>
//               <Stack direction="row" spacing={1} alignItems="center">
//                 <PhoneIcon fontSize="small" />
//                 <Link href="tel:+917479708899" underline="none" sx={{ color: "text.secondary" }}>
//                   (+91) 7479708899
//                 </Link>
//               </Stack>
//               <Stack direction="row" spacing={1} alignItems="center">
//                 <EmailIcon fontSize="small" />
//                 <Link href="mailto:aisectintercollege2023@gmail.com" underline="none" sx={{ color: "text.secondary" }}>
//                   aisectintercollege2023@gmail.com
//                 </Link>
//               </Stack>
//             </Stack>

//             <Typography
//               variant="subtitle2"
//               sx={{ fontWeight: 800, mt: 1.5, mb: 0.5, fontSize: { xs: 13, sm: 13.5 } }}
//             >
//               Full Address
//             </Typography>

//             <address style={{ margin: 0 }}>
//               <Stack spacing={0.75} sx={{ color: "text.secondary", fontSize: { xs: 13, sm: 13.5 } }}>
//                 <Stack direction="row" spacing={1.2} alignItems="flex-start">
//                   <LocationOnIcon fontSize="small" />
//                   <span>
//                     <strong>Main Campus:</strong> Vill: Jonhiya (Tarba–Kharba), P.O.: Maheshra,
//                     P.S.: Daru, Dist.: Hazaribagh, Jharkhand – 825313
//                   </span>
//                 </Stack>
//                 <Stack direction="row" spacing={1.2} alignItems="flex-start">
//                   <LocationOnIcon fontSize="small" />
//                   <span>
//                     <strong>City Campus:</strong> AISECT University, Matwari Chowk,
//                     Hazaribagh, Jharkhand – 825301
//                   </span>
//                 </Stack>
//               </Stack>
//             </address>
//           </Grid>
//         </Grid>
//       </Container>

//       <Divider sx={{ opacity: 0.8 }} />

//       {/* Bottom */}
//       <Container maxWidth="lg" sx={{ py: 1.5 }}>
//         <Stack
//           direction={{ xs: "column", sm: "row" }}
//           spacing={0.75}
//           alignItems="center"
//           justifyContent="space-between"
//         >
//           <Typography
//             sx={{
//               fontSize: { xs: 12.5, sm: 13 },
//               textAlign: { xs: "center", sm: "left" },
//               color: "text.secondary",
//             }}
//           >
//             ©2023-{year}{" "}
//             <Link component={RouterLink} to="/" underline="hover" sx={{ color: "text.primary" }}>
//               AISECT Inter College
//             </Link>{" "}
//             — All rights reserved.
//           </Typography>

//           <Stack direction="row" spacing={1}>
//             <Link href="#!" underline="hover" sx={{ fontSize: { xs: 12.5, sm: 13 }, color: "text.secondary" }}>
//               Terms of Service
//             </Link>
//             <Typography sx={{ color: "text.disabled" }}>•</Typography>
//             <Link href="#!" underline="hover" sx={{ fontSize: { xs: 12.5, sm: 13 }, color: "text.secondary" }}>
//               Privacy Policy
//             </Link>
//           </Stack>
//         </Stack>
//       </Container>
//     </Box>
//   );
// }


// import React from "react";
// import { Link } from "react-router-dom";
// import {
//   Box,
//   Card,
//   CardContent,
//   CardMedia,
//   IconButton,
//   Typography,
// } from "@mui/material";
// import BookmarkIcon from "@mui/icons-material/Bookmark";

// export default function Footer() {
//   return (
//     <Box sx={{ borderTop: 1 }}>
//       <footer
//         className="rbt-footer footer-style-1 bg-color-white overflow-hidden"
//         sx={{
//           display: "flex",
//           flexDirection: "column",
//           backgroundColor: " #E2D1F9",
//         }}
//       >
//         <Box className="footer-top">
//           <Box
//             className="container"
//             sx={{
//               display: "flex",
//               flexDirection: { xs: "column", md: "row" },
//               gap: { xs: 0, md: 20 },
//               alignItems: { xs: "center", md: "flex-start" },
//               height: { xs: "auto", md: 260 },
//               pb: 3,
//             }}
//           >
//             <div className="col-lg-4 col-md-6 col-sm-6 col-12">
//               <div className="footer-widget">
//                 <div className="logo">
//                   <Link to="/">
//                     <img src="assets/images/logo/aisect_inter_college_logo.png" alt="Edu-cause" />
//                   </Link>
//                 </div>
//                 <h6 className="description mt--20 mb--20 pt--15">
//                   We are always in search for talented and motivated people.
//                   Don't be shy, introduce yourself!
//                 </h6>
//               </div>
//             </div>

//             <div className="col-lg-2 col-md-6 col-sm-6 col-12">
//               <div className="footer-widget">
//                 <h5 className="ft-title">Our College</h5>
//                 <ul className="ft-link">
//                   <li>
//                     <Link to="/">Home</Link>
//                   </li>
//                   <li>
//                     <Link to="/about">About us</Link>
//                   </li>
//                   <li>
//                     <a href="/admission">Admission</a>
//                   </li>
//                   <li>
//                     <a href="/event">Event</a>
//                   </li>
//                   <li>
//                     <a href="/circular">Circular</a>
//                   </li>

//                   <li>
//                     <Link to="/contact">Contact Us</Link>
//                   </li>
//                 </ul>
//               </div>
//               <Box>
//                 <ul className="social-icon social-default justify-content-start mb--20 pt--20">
//                   <li>
//                     <a href="https://www.facebook.com/">
//                       <i className="feather-facebook" />
//                     </a>
//                   </li>
//                   {/* <li>
//                     <a href="https://www.twitter.com/">
//                       <i className="feather-twitter" />
//                     </a>
//                   </li> */}
//                   <li>
//                     <a href="https://www.instagram.com/">
//                       <i className="feather-instagram" />
//                     </a>
//                   </li>
//                   <li>
//                     <a href="https://www.linkdin.com/">
//                       <i className="feather-linkedin" />
//                     </a>
//                   </li>
//                 </ul>
//               </Box>
//             </div>
//             <div className="col-lg-4 col-md-6 col-sm-6 col-12">
//               <div className="footer-widget">
//                 <h5 className="ft-title">Get Contact</h5>
//                 <ul className="ft-link">
//                   <li>
//                     <span>Phone:</span> <a href="#">(+91) 9999999999</a>
//                   </li>
//                   <li>
//                     <span>E-mail:</span>{" "}
//                     <a href="mailto:hr@example.com">aisectintercollege2023@gmail.com</a>
//                   </li>
//                 </ul>
//                 <form className="newsletter-form mt--20 mr--20" action="#">
//                   <h6 className="w-600">Full Address</h6>
//                   <p className="description">
//                     <span>Main Campus :</span> Vill: Jonhiya, (Tarba-Kharba),
//                     P.O. : Maheshra,P.S. : Daru, Dist. : Hazaribagh, Jharkhand -
//                     825313
//                     <br />
//                     <span>City Campus :</span> AISECT University, Matwari Chowk,
//                     Hazaribagh, Jharkhand - 825301
//                   </p>
//                 </form>
//               </div>
//             </div>
//           </Box>
//         </Box>
//         <div className="rbt-separator-mid">
//           <div className="container">
//             <hr className="rbt-separator m-0" />
//           </div>
//         </div>
//         <Box
//           className="copyright-area copyright-style-1 pt--20"
//           sx={{
//             mt: 2,
//             py: 2,
//             backgroundColor: "#f5f5f5",
//           }}
//         >
//           <Box className="container">
//             <Box
//               sx={{
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "space-between",
//               }}
//             >
//               <h6
//                 // variant="body1"
//                 className="rbt-link-hover text-center text-lg-start"
//               >
//                 Copyright © 2023 <a href="#!">AISECT University</a> All Rights
//                 Reserved
//               </h6>
//               <ul className="copyright-link rbt-link-hover">
//                 <li>
//                   <a href="#">Terms of service</a>
//                 </li>
//                 <li>
//                   <a href="#">Privacy policy</a>
//                 </li>
//                 {/* <li>
//                   <a href="#!">Login &amp; Register</a>
//                 </li> */}
//               </ul>
//             </Box>
//           </Box>
//         </Box>
//       </footer>
//       <div className="rbt-progress-parent">
//         <svg
//           className="rbt-back-circle svg-inner"
//           width="100%"
//           height="100%"
//           viewBox="-1 -1 102 102"
//         >
//           <path d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98" />
//         </svg>
//       </div>
//     </Box>
//   );
// }
